import { CloseOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { RemoteSearchInput } from '../RemoteSearchInput/RemoteSearchInput';
import styles from './LandingPagesRemoteSearch.module.css';

type Props = {
  remoteLandings?: string[];
  title: string;
  placeholder?: string;
  searchDisabled?: boolean;
  className?: string;
  onFetch: (query: string) => Promise<string[]>;
  onSelect?: (item: string) => void;
  onDelete?: (item: string) => void;
};

export const LandingPagesRemoteSearch = ({
  remoteLandings,
  title,
  placeholder,
  searchDisabled,
  className,
  onFetch,
  onSelect,
  onDelete,
}: Props) => {
  const [urlSearchValue, setUrlSearchValue] = useState('');
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [fetchUrlResults, setFetchUrlResults] = useState<string[]>([]);

  const clear = () => {
    setUrlSearchValue('');
    setFetchUrlResults([]);
  };

  const onSelectItem = (selectedItem: string) => {
    const isExisting = selectedUrls.find(url => url === selectedItem);
    if (isExisting) return;
    setSelectedUrls(state => [...state, selectedItem]);
    onSelect?.(selectedItem);
    clear();
  };

  const onDeleteItem = (url: string) => {
    setSelectedUrls(state => state.filter(item => item !== url));
    onDelete?.(url);
  };

  const renderSuggestion = (suggestion: string) => {
    if (!suggestion) return null;
    return (
      <div className={styles.suggestionContainer} key={suggestion}>
        <div className={styles.suggestion}>{suggestion}</div>
      </div>
    );
  };

  const fetchLandings = async (query: string) => {
    const trimmedQuery = query.trim();
    setUrlSearchValue(trimmedQuery);
    const results = await onFetch(trimmedQuery);
    setFetchUrlResults(results || []);
  };

  useEffect(() => {
    if (remoteLandings !== selectedUrls) {
      setSelectedUrls(remoteLandings || []);
    }
  }, [remoteLandings]);

  return (
    <div className={classNames(styles.block, className)}>
      <div className={styles.title}>{title}</div>
      <div className={styles.searchContainer}>
        <RemoteSearchInput
          value={urlSearchValue}
          clearInputOnSelect
          placeholder={placeholder}
          disabled={searchDisabled}
          renderSuggestion={renderSuggestion}
          onFetch={fetchLandings}
          results={fetchUrlResults}
          onSelectedItem={onSelectItem}
          className={styles.input}
          style={{
            border: '1px solid var(--color-gray-5)',
            borderRadius: '4px',
          }}
          withSearchIcon
        />
        <div className={styles.selectedValues}>
          {selectedUrls?.map(url => (
            <div className={styles.itemWrapper} key={url}>
              <Tooltip title={url.length > 50 ? url.toLocaleLowerCase() : ''}>
                <div className={styles.selectedUrl} key={url}>
                  {url.toLocaleLowerCase()}
                </div>
              </Tooltip>
              <CloseOutlined
                className={styles.close}
                onClick={() => onDeleteItem(url)}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
